import React, { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { CinemographHeroProps, CinemographHeroModel } from './CinemographHero.types';
import { StyledCinemographHero } from './CinemographHero.styles';
import { Image } from '@marriott/mi-ui-library';
import { debounce, useDCADynamicModelAlloy } from '../../modules/helpers';

interface PlayerInstance {
  paused: () => boolean;
  pause: () => void;
  play: () => void;
  controls_?: boolean;
}

const BRIGHTCOVE_PLAYER_OPTIONS = {
  autoplay: true,
  muted: true,
  aspectRatio: '16:9',
  loop: true,
  preload: 'auto',
  src: 'https://players.brightcove.net',
} as const;

const MOBILE_MQ = '(max-width: 767px)';

const ReactPlayerLoader = dynamic(() => import('@brightcove/react-player-loader'), {
  ssr: false,
});

type LoaderSuccess = { ref?: PlayerInstance };

export const CinemographHero: FC<CinemographHeroProps> = props => {
  const [player, setPlayer] = useState<PlayerInstance | null>(null);
  const [viewPort, setViewPort] = useState('');
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);
    handleResize();
    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 767px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else {
      setViewPort('viewPortM');
    }
  };

  const mboxParam = props?.model?.mboxParameter;
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  const resolvedModel: CinemographHeroModel = useMemo(
    () => (targetData ? targetData : props?.model),
    [targetData, props?.model]
  );

  const playLabel = resolvedModel?.playLabel;
  const pauseLabel = resolvedModel?.pauseLabel;

  const videoId = useMemo(() => {
    const ap = resolvedModel?.assetProperties;
    if (!ap) return '';
    if (viewPort === 'Mobile') {
      return (ap?.brightcoveMobileVideoId ?? ap?.brightcoveVideoId) as string;
    }
    return ap?.brightcoveVideoId as string;
  }, [resolvedModel?.assetProperties, viewPort]);

  const imageRenditions = useMemo(() => {
    if (!resolvedModel?.dynamicImage) return undefined;
    if (viewPort !== 'Mobile') return resolvedModel?.dynamicImage?.renditions;
    return resolvedModel?.dynamicMobileImage?.renditions ?? resolvedModel?.dynamicImage?.renditions;
  }, [resolvedModel, viewPort]);

  const attachAnalyticsAttr = useCallback((success: LoaderSuccess) => {
    const { ref } = success;
    if (!ref) return;
    ref.controls_ = false;
    setPlayer(ref);
    requestAnimationFrame(() => {
      if (isMountedRef.current) {
        setVideoLoading(false);
      }
    });
  }, []);

  const togglePlayback = useCallback(() => {
    if (!player) return;
    if (player.paused()) {
      player.play();
      setVideoPlaying(true);
    } else {
      player.pause();
      setVideoPlaying(false);
    }
  }, [player]);

  const handlePlayPause = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
      if (event.type !== 'click' && (!('key' in event) || event.key !== 'Enter')) return;
      togglePlayback();
    },
    [togglePlayback]
  );

  return (
    <StyledCinemographHero className="m-container-fullbleed">
      <div
        className={clsx(
          resolvedModel?.assetProperties?.videoAccountId
            ? `loader-cinemograph loader-cinemograph-${resolvedModel?.assetProperties?.variation} color-scheme2`
            : '',
          resolvedModel?.dynamicImage ? 'loader-cinemograph loader-cinemograph-image' : ''
        )}
      >
        <span
          className={clsx(
            'brand-logo-luxury-Mark cinemograph-loading-logo',
            videoLoading ? 'noanimation' : 'animate-logo',
            resolvedModel?.assetProperties?.videoAccountId ? '' : 'd-none'
          )}
        ></span>
        <span
          className={clsx(
            videoLoading ? 'sr-only' : 'd-none',
            resolvedModel?.assetProperties?.videoAccountId ? '' : 'd-none'
          )}
        >
          brand-logo-luxury-Mark
        </span>
        {resolvedModel?.activateOverlay && (
          <span className="row overlay_text p-1">
            <span className="icon-location t-label-xs mr-1" />
            <a href={resolvedModel?.overlayPath} className="t-label-xs">
              {resolvedModel?.overlayTitle}
            </a>
          </span>
        )}
        {resolvedModel?.dynamicImage && viewPort !== '' && (
          <Image
            title=""
            altText={resolvedModel?.dynamicImage?.altText}
            defaultImageURL={resolvedModel?.dynamicImage?.assetPath}
            renditions={imageRenditions}
            customClass={clsx('cinemograph-image')}
            loading="eager"
            fetchpriority="high"
          />
        )}

        {!resolvedModel?.dynamicImage && resolvedModel?.assetProperties?.videoAccountId && viewPort !== '' && (
          <div
            className={clsx(
              'video-container',
              resolvedModel?.overlapNavigation && 'video-overlay',
              `cinemograph-${resolvedModel?.assetProperties?.variation}`
            )}
          >
            <ReactPlayerLoader
              id={`luxury-cinemograph-${resolvedModel?.assetProperties?.videoAccountId}`}
              accountId={resolvedModel?.assetProperties?.videoAccountId as string}
              options={BRIGHTCOVE_PLAYER_OPTIONS}
              videoId={videoId}
              playerId={resolvedModel?.assetProperties?.playerId ?? 'default'}
              onSuccess={attachAnalyticsAttr}
            />
            <div
              className={clsx(
                'icon-play-pause custom_click_track ',
                resolvedModel?.typeOfVideoControl === 'dark-theme' && 'dark-video-control'
              )}
              onKeyDown={handlePlayPause}
              onClick={handlePlayPause}
              tabIndex={0}
              custom_click_track_value={`${resolvedModel?.assetProperties?.assetType} | ${
                videoPlaying ? pauseLabel : playLabel
              } button | internal`}
              role="button"
            >
              {!videoLoading && !videoPlaying && (
                <span>
                  <span className="icon-l icon-play"></span>
                  <span className="sr-only">{playLabel}</span>
                </span>
              )}
              {!videoLoading && videoPlaying && (
                <span>
                  <span className="icon-l icon-pause"></span>
                  <span className="sr-only">{pauseLabel}</span>
                </span>
              )}
            </div>
            {resolvedModel?.applyScrim && <div className="cinemograph--scrim-component"></div>}
          </div>
        )}
      </div>
    </StyledCinemographHero>
  );
};

export const CinemographHeroConfig = {
  emptyLabel: 'CinemographHero',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cinemograph-hero/v1/cinemograph-hero`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CinemographHeroEditable = (props: any) => (
  <div data-testid="luxury-CinemographHero" data-component-name="m-luxury-CinemographHero">
    <EditableComponent config={CinemographHeroConfig} {...props}>
      <CinemographHero {...props} />
    </EditableComponent>
  </div>
);
